/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { useEffect, useState } from "react";
import Modal from "react-modal";
import { theme } from "styles/theme";
import Button from "uis/Button";
import Icon from "uis/Icon";
import InputField from "uis/InputField";
import CheckBox from "uis/CheckBox";
import { Annotation } from "pages/visitors/visit-guide-manage/uis";
import { TMessengerChannel } from "apis/visitors/type";
import UI from "@mile-meeting/mile-ui";
import { getSlackChannelInfo, TSlackChannel } from "apis/messangers";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    width: "550px",
    borderRadius: "20px",
    transform: "translate(-50%, -50%)",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

Modal.setAppElement("#root");

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  registered: TMessengerChannel[];
  onUpdate?: (value: TSlackChannel) => void;
};

const Title = styled.p`
  font-size: 20px;
  font-weight: 400;
`;

export function AddChannelModal(props: Props) {
  const { modalIsOpen, closeModal, registered, onUpdate = () => {} } = props;
  const [channel, setChannel] = useState<TSlackChannel | null>(null);
  const [channelName, setChannelName] = useState<string | null>(null);

  const [link, setLink] = useState<string>();
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState<boolean>(false);

  const onHandleVerifyLink = async (link?: string) => {
    if (!link) {
      alert("메신저 채널 링크를 붙여넣어주세요.");
      return;
    }
    verifySlackLink(link);
  };

  const verifySlackLink = async (link: string) => {
    const res = await getSlackChannelInfo(link);
    if (!res.success) {
      setError("존재하지 않는 슬랙 채널입니다.");
    } else {
      const registered_slack_ids = registered?.map((item) => item.channelId);
      if (registered_slack_ids?.includes(res.data?.slackChannelId ?? "")) {
        setError("이미 등록된 채널입니다");
      } else {
        setChecked(true);
        setError(null);
        setChannel(res?.data ?? null);
        setChannelName(res.data?.slackChannelName ?? "");
      }
    }
  };

  useEffect(() => {
    setChannel(null);
    setChannelName(null);
    setLink("");
    setError(null);
    setChecked(false);
  }, [modalIsOpen]);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
        `}
      >
        <UI.IconButton.Normal
          type="primary"
          icon="x"
          size={20}
          color="var(--alias-color-label-normal)"
          onClick={closeModal}
        />
      </div>
      <div
        css={css`
          display: flex;
          flex: 1;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          margin: 30px 10px;
        `}
      >
        <Title>추가할 슬랙 채널 링크를 붙여넣어주세요.</Title>
        <div
          css={css`
            width: 100%;
          `}
        >
          {/* TODO: 추후 가이드 링크 연결 예정  */}
          {/* <Annotation
            onClick={() => window.open("https://mile.mintlify.app/essentials/common/link-slack-channel")}
            css={css`
              display: flex;
              gap: 4px;
              padding-left: 0;
              align-items: center;
              &:hover {
                color: ${theme.neutral600};
              }

              cursor: pointer;
            `}
          >
            <Icon name="question" color={theme.neutral400} width={20} />
            슬랙 채널 추가하기 도움말
          </Annotation> */}
          <div
            css={css`
              display: flex;
              width: 100%;
              gap: 12px;
            `}
          >
            <InputField
              errorText={error ? error : ""}
              placeholder={"채널 링크"}
              onChange={(value) => setLink(value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onHandleVerifyLink(e.target.value);
              }}
              type={error ? "error" : "default"}
            />

            <Button
              onClick={() => onHandleVerifyLink(link)}
              title="조회"
              type="secondary"
              styles={{
                container: css`
                  width: 120px;
                  height: 46px;
                `,
              }}
            />
          </div>
        </div>
        {channel && (
          <div
            onClick={() => setChecked(!checked)}
            css={css`
              display: flex;
              overflow-y: auto;
              align-items: center;
              gap: 12px;
              width: calc(100% - 48px);
              border: ${theme.neutral50} 1px solid;
              border-radius: 10px;
              padding: 12px 24px;
              &:hover {
                background-color: ${theme.neutral50};
              }
            `}
          >
            <CheckBox value={checked} />
            {channelName}
          </div>
        )}
        <Button
          onClick={() => {
            if (checked && channel) {
              onUpdate(channel);
              closeModal();
            } else {
              alert("추가할 채널을 선택해주세요.");
            }
          }}
          title="추가하기"
          styles={{
            container: css`
              width: 100%;
              padding: 16px 0;
            `,
          }}
        />
      </div>
    </Modal>
  );
}
